import { AppSettings } from "@/AppSettings";
import { CoordinatingGroup, Institution, MasterGroup, Participant, SourceDataFormStatus, SourceDataReviewAndVerificationUpdate, Trial, UpdateCaseReportFormState } from "@/api/dtos";
import { IReviewAndVerificationItemData } from "@/components/forms/ReviewAndVerificationForm";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import SourceDataFormSettingsContext from "@/contexts/form/SourceDataFormSettingsContext";
import { ItemType } from "@/hooks/form/configuration/useItemConfigurations";
import { Item, ItemGroup } from "@/hooks/form/source-data/useItemsVirtualized";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, styled } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useContext, useMemo } from "react";
import { ButtonPlaceholder, CounterButton, CounterButtonBox, StyledTooltip } from ".";
import useForm from "@/hooks/form/source-data/useForm";

const Container = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2)
}));

interface ISourceDataFormReviewAndVerificationAllButtonProps {
    form: ReturnType<typeof useForm>;
    trial: Trial;
    masterGroup?: MasterGroup;
    coordinatingGroup?: CoordinatingGroup;
    institution?: Institution;
    participant: Participant;
    studyEventOid: string;
    studyEventRepeat: number;
    caseReportFormOid: string;
    caseReportFormVersionOid: string;
    itemData: Array<Item | ItemGroup>;
    allowReview?: boolean;
    allowVerification?: boolean;
    allowNotApplicable?: boolean;
}

const SourceDataFormReviewAndVerificationAllButton: FC<ISourceDataFormReviewAndVerificationAllButtonProps> = ({
    trial,
    masterGroup,
    coordinatingGroup,
    institution,
    participant,
    studyEventOid,
    studyEventRepeat,
    caseReportFormOid,
    caseReportFormVersionOid,
    form,
    itemData,
    allowReview,
    allowVerification,
    allowNotApplicable
}) => {

    const client = useContext(ServiceStackClientContext);

    const { 
        settings: { 
            hideReviews,
            hideVerifications,
            hideNotApplicables,
        } 
    } = useContext(SourceDataFormSettingsContext);

    const propertyPath = useMemo(() => {
        if (!studyEventOid ||
            !studyEventRepeat ||
            !caseReportFormOid
        ) {
            return 'data.unknown'
        }

        return `data.${studyEventOid}.${studyEventRepeat}.${caseReportFormOid}`;
    }, [
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid
    ])

    const { 
        state: {
            value: data 
        },
        context: {
            setValue: setData,
            getValue: getData
        }
    } = useScopedField<Record<string, Record<number, Record<string, IReviewAndVerificationItemData>>>>(propertyPath, { value: true });

    const { 
        state: {
            value: loading 
        },
        context: {
            setValue: setLoading
        }
    } = useScopedField<boolean>('loading', { value: true });

    //const deferredData = useDeferredValue(data, )

    // const {
    //     hasReview,
    //     hasVerification,
    //     allReviewed,
    //     allVerified,
    //     reviewLoading,
    //     verificationLoading
    // } = useMemo(() => {
    //     let hasReview = false;
    //     let hasVerification = false;
    //     let allReviewed = true;
    //     let allVerified = true;
    //     let reviewLoading = false;
    //     let verificationLoading = false;

    //     for (let itemGroupName in data) {
    //         for (let itemGroupRepeatStr in data[itemGroupName]) {
    //             const itemGroupRepeat = +itemGroupRepeatStr;

    //             for (let itemName in data[itemGroupName][itemGroupRepeat]) {
    //                 const item = data[itemGroupName][itemGroupRepeat][itemName];

    //                 if (!!item.review) {
    //                     hasReview = true;

    //                     if (!item.review?.reviewed) {
    //                         allReviewed = false;
    //                     }

    //                     if (item.reviewLoading) {
    //                         reviewLoading = true;
    //                     }
    //                 }

    //                 if (!!item.verification) {
    //                     hasVerification = true;

    //                     if (!item.verification?.verified) {
    //                         allVerified = false;
    //                     }

    //                     if (item.verificationLoading) {
    //                         verificationLoading = true;
    //                     }
    //                 }
    //             }
    //         }
    //     }

    //     return {
    //         hasReview,
    //         hasVerification,
    //         allReviewed,
    //         allVerified,
    //         reviewLoading,
    //         verificationLoading
    //     }
    // }, [data]);
    
    const {
        hasReview,
        hasVerification,
        hasNotApplicable,
        allReviewed,
        allVerified,
        allNotApplicable,
        reviewLoading,
        verificationLoading,
        notApplicableLoading
    } = useMemo(() => {
        
        let hasReview = false;
        let hasVerification = false;
        let hasNotApplicable = false;
        let allReviewed = true;
        let allVerified = true;
        let allNotApplicable = true;
        let reviewLoading = false;
        let verificationLoading = false;
        let notApplicableLoading = false;
        let reviewTotal = 0;
        let reviewComplete = 0;
        let verificationTotal = 0;
        let verificationComplete = 0;
        let verificationNotApplicableCount = 0;
        let reviewNotApplicableCount = 0;
        let notApplicableCount = 0;

        if (!allowReview && !allowVerification && !allowNotApplicable) {
            return {
                hasReview,
                hasVerification,
                hasNotApplicable,
                allReviewed,
                allVerified,
                allNotApplicable,
                reviewLoading,
                verificationLoading,
                notApplicableLoading,
                reviewComplete,
                reviewTotal,
                verificationComplete,
                verificationTotal,
                verificationNotApplicableCount,
                reviewNotApplicableCount,
                notApplicableCount
            }
        }

        for (let item of itemData) {
            if (item.type == ItemType.ItemGroup) {
                continue;
            }

            const state = data?.[item.item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.item.itemGroupRepeat]?.[item.item.itemName!];

            const isDisabled = false; //!item.item.sourceDataItem;

            const allowItemReview = !isDisabled && 
                allowReview &&
                item.item.configuration?.allowReview && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowReview);

            const allowItemNotApplicable = !isDisabled && 
                allowNotApplicable &&
                item.item.configuration?.allowNotApplicable && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowNotApplicable);

            const allowItemVerification = !isDisabled && 
                allowVerification &&
                item.item.configuration?.allowVerification && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowVerification);
            
            if(allowItemNotApplicable) {
                hasNotApplicable = true;

                
                if (!state?.notApplicable?.notApplicable) {
                    allNotApplicable = false;
                }
                else {
                    if (allowItemReview) {
                        reviewNotApplicableCount++;
                    }

                    if (allowItemVerification) {
                        verificationNotApplicableCount++;
                    }

                    notApplicableCount++;
                }

                if (state?.notApplicableLoading) {
                    notApplicableLoading = true;
                }
            }

            if(allowItemNotApplicable && state?.notApplicable?.notApplicable) {
                continue;
            }
            
            if (allowItemReview) {
                hasReview = true;

                reviewTotal++;

                if (!state?.review?.reviewed) {
                    allReviewed = false;
                }
                else {
                    reviewComplete++;
                }

                if (state?.reviewLoading) {
                    reviewLoading = true;
                }
            }

            if (allowItemVerification) {
                hasVerification = true;

                verificationTotal++;

                if (!state?.verification?.verified) {
                    allVerified = false;
                }
                else {
                    verificationComplete++;
                }

                if (state?.verificationLoading) {
                    verificationLoading = true;
                }
            }
            
            if(allowItemNotApplicable) {
                hasNotApplicable = true;

                
                if (!state?.notApplicable?.notApplicable) {
                    allNotApplicable = false;
                }
                else {
                    notApplicableCount++;
                }

                if (state?.notApplicableLoading) {
                    notApplicableLoading = true;
                }
            }
        }



        return {
            hasReview,
            hasVerification,
            hasNotApplicable,
            allReviewed,
            allVerified,
            allNotApplicable,
            reviewLoading,
            verificationLoading,
            notApplicableLoading,
            reviewComplete,
            reviewTotal,
            verificationComplete,
            verificationTotal,
            notApplicableCount
        }
    }, [data, itemData, allowReview, allowVerification, allowNotApplicable]);

    const onReviewClick = async () => {
        
        setLoading(true);

        const updates: SourceDataReviewAndVerificationUpdate[] = [];

        for (let item of itemData) {

            if (item.type == ItemType.ItemGroup) {
                continue;
            }
            let hasReview = false;
            let hasVerification = false;

            let reviewed: boolean | undefined = undefined;
            let verified: boolean | undefined = undefined;

            const state = data?.[item.item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.item.itemGroupRepeat]?.[item.item.itemName!];

            const allowItemNotApplicable = 
                allowNotApplicable &&
                item.item.configuration?.allowNotApplicable && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowNotApplicable);

            if(allowItemNotApplicable && state?.notApplicable?.notApplicable) {
                continue;
            }

            const isDisabled = false; //!item.item.sourceDataItem;


            const allowItemReview = !isDisabled && 
                allowReview &&
                item.item.configuration?.allowReview && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowReview);

            if (allowItemReview) {
                hasReview = true;

                if (!state?.review?.reviewed) {
                    reviewed = false;
                }
                else {
                    reviewed = true;
                }
            }


            const allowItemVerification = !isDisabled && 
                allowVerification &&
                item.item.configuration?.allowVerification && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowVerification);

            if (allowItemVerification) {
                hasVerification = true;

                if (!state?.verification?.verified) {
                    verified = false;
                }
                else {
                    verified = true;
                }
            }

            if (hasVerification || hasReview) {
                const newValue = !allReviewed;

                updates.push(new SourceDataReviewAndVerificationUpdate({
                    itemGroupName: item.item?.itemGroupName,
                    itemGroupRepeat: item.item?.itemGroupRepeat,
                    itemName: item.item.itemName,
                    priority: item.item.priority,
                    value: item.item.sourceDataItem?.value,
                    reviewed: reviewed === undefined ? undefined : newValue,
                    verified: !newValue && verified ? false : verified
                }));
            }
        }

        if (updates.length > 0) {
            
            try {
                const response = await client.post(new UpdateCaseReportFormState({
                    trialId: trial?.id,
                    masterGroupId: masterGroup?.id,
                    coordinatingGroupId: coordinatingGroup?.id,
                    institutionId: institution?.id,
                    participantId: participant?.id,
                    studyEventOid: studyEventOid,
                    studyEventRepeat: studyEventRepeat,
                    caseReportFormOid: caseReportFormOid,
                    caseReportFormVersionOid: caseReportFormVersionOid,
                    updates: updates
                }));
            }
            catch {

            }
        }

        setLoading(false);
    };

    const onVerificationClick = async () => {
        setLoading(true);

        const updates: SourceDataReviewAndVerificationUpdate[] = [];

        for (let item of itemData) {
            if (item.type == ItemType.ItemGroup) {
                continue;
            }

            let hasReview = false;
            let hasVerification = false;

            let reviewed: boolean | undefined = undefined;
            let verified: boolean | undefined = undefined;

            const state = data?.[item.item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.item.itemGroupRepeat]?.[item.item.itemName!];

            const isDisabled = false; //!item.item.sourceDataItem;

            const allowItemReview = !isDisabled && 
                allowReview &&
                item.item.configuration?.allowReview && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowReview);

            const allowItemNotApplicable = 
                allowNotApplicable &&
                item.item.configuration?.allowNotApplicable && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowNotApplicable);

            if(allowItemNotApplicable && state?.notApplicable?.notApplicable) {
                continue;
            }

            if (allowItemReview) {
                hasReview = true;

                if (!state?.review?.reviewed) {
                    reviewed = false;
                }
                else {
                    reviewed = true;
                }
            }


            const allowItemVerification = !isDisabled && 
                allowVerification &&
                item.item.configuration?.allowVerification && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowVerification);

            if (allowItemVerification) {
                hasVerification = true;

                if (!state?.verification?.verified) {
                    verified = false;
                }
                else {
                    verified = true;
                }
            }

            if (hasVerification || hasReview) {
                const newValue = !allVerified;

                updates.push(new SourceDataReviewAndVerificationUpdate({
                    itemGroupName: item.item?.itemGroupName,
                    itemGroupRepeat: item.item?.itemGroupRepeat,
                    itemName: item.item.itemName,
                    priority: item.item.priority,
                    value: item.item.sourceDataItem?.value,
                    reviewed: newValue && reviewed === false ? true : reviewed,
                    verified: verified === undefined ? undefined : newValue,
                    missing: !item.item.sourceDataItem
                }));
            }
        }

        if (updates.length > 0) {
            
            try {
                const response = await client.post(new UpdateCaseReportFormState({
                    trialId: trial?.id,
                    masterGroupId: masterGroup?.id,
                    coordinatingGroupId: coordinatingGroup?.id,
                    institutionId: institution?.id,
                    participantId: participant?.id,
                    studyEventOid: studyEventOid,
                    studyEventRepeat: studyEventRepeat,
                    caseReportFormOid: caseReportFormOid,
                    caseReportFormVersionOid: caseReportFormVersionOid,
                    updates: updates
                }));
            }
            catch {

            }
        }

        setLoading(false);
    };

    const onNotApplicableClick = async () => {
        setLoading(true);

        const updates: SourceDataReviewAndVerificationUpdate[] = [];

        for (let item of itemData) {
            if (item.type == ItemType.ItemGroup) {
                continue;
            }

            let hasReview = false;
            let hasVerification = false;
            let hasNotApplicable = false;

            let reviewed: boolean | undefined = undefined;
            let verified: boolean | undefined = undefined;
            let notApplicable: boolean | undefined = undefined;

            const state = data?.[item.item?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey]?.[item.item.itemGroupRepeat]?.[item.item.itemName!];

            const isDisabled = false; //!item.item.sourceDataItem;

            const allowItemReview = !isDisabled && 
                allowReview &&
                item.item.configuration?.allowReview && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowReview);

            if (allowItemReview) {
                hasReview = true;

                if (!state?.review?.reviewed) {
                    reviewed = false;
                }
                else {
                    reviewed = true;
                }
            }


            const allowItemVerification = !isDisabled && 
                allowVerification &&
                item.item.configuration?.allowVerification && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowVerification);

            if (allowItemVerification) {
                hasVerification = true;

                if (!state?.verification?.verified) {
                    verified = false;
                }
                else {
                    verified = true;
                }
            }


            const allowItemNotApplicable = !isDisabled && 
                allowNotApplicable &&
                item.item.configuration?.allowNotApplicable && 
                (item.itemGroup === undefined || item.itemGroup?.configuration?.allowNotApplicable);

            if (allowItemNotApplicable) {
                hasNotApplicable = true;

                if (!state?.notApplicable?.notApplicable) {
                    notApplicable = false;
                }
                else {
                    notApplicable = true;
                }
            }

            if (hasVerification || hasReview || hasNotApplicable) {
                const newValue = !allNotApplicable;

                updates.push(new SourceDataReviewAndVerificationUpdate({
                    itemGroupName: item.item?.itemGroupName,
                    itemGroupRepeat: item.item?.itemGroupRepeat,
                    itemName: item.item.itemName,
                    priority: item.item.priority,
                    value: item.item.sourceDataItem?.value,
                    reviewed: newValue && reviewed === true ? false : reviewed,
                    verified: newValue && verified === true ? false : verified,
                    notApplicable: notApplicable === undefined ? undefined : newValue,
                    missing: !item.item.sourceDataItem
                }));
            }
        }

        if (updates.length > 0) {
            
            try {
                const response = await client.post(new UpdateCaseReportFormState({
                    trialId: trial?.id,
                    masterGroupId: masterGroup?.id,
                    coordinatingGroupId: coordinatingGroup?.id,
                    institutionId: institution?.id,
                    participantId: participant?.id,
                    studyEventOid: studyEventOid,
                    studyEventRepeat: studyEventRepeat,
                    caseReportFormOid: caseReportFormOid,
                    caseReportFormVersionOid: caseReportFormVersionOid,
                    updates: updates
                }));
            }
            catch {

            }
        }

        setLoading(false);
    };

    const disabled = !form?.sourceDataForm || form.sourceDataForm.status === SourceDataFormStatus.None || form.sourceDataForm.status === SourceDataFormStatus.Skipped;

    return (
        <Container>
            {
                !hideReviews && allowReview ?
                    hasReview ?
                        <Box>
                            <StyledTooltip
                                title={
                                    disabled ? 
                                        'Form does not exist in OpenClinica' :
                                        allReviewed ? 
                                            'Reviewed' : 
                                            'Not Reviewed'
                                }
                                followCursor
                            >
                                <CounterButtonBox>
                                    <CounterButton
                                        variant={
                                            allReviewed ?
                                                'contained' :
                                                'outlined'
                                        }
                                        color={
                                            allReviewed ?
                                                'success' :
                                                'primary'
                                        }
                                        loading={!!reviewLoading || !!loading}
                                        disabled={disabled}
                                        onClick={onReviewClick}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.review} size="2x" />
                                    </CounterButton>
                                </CounterButtonBox>
                            </StyledTooltip>
                        </Box>:
                        <ButtonPlaceholder /> :
                    null
            }
            {
                !hideVerifications && allowVerification ?
                    hasVerification ?
                        <Box>
                            <StyledTooltip
                                title={
                                    disabled ? 
                                        'Form does not exist in OpenClinica' :
                                        allVerified ? 
                                            'Verified' : 
                                            'Not Verified'
                                }
                                followCursor
                            >
                                <CounterButtonBox>
                                    <CounterButton
                                        variant={
                                            allVerified ?
                                                'contained' :
                                                'outlined'
                                        }
                                        color={
                                            allVerified ?
                                                'success' :
                                                'primary'
                                        }
                                        loading={!!verificationLoading || !!loading}
                                        onClick={onVerificationClick}
                                        disabled={disabled}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.verification} size="2x" />
                                    </CounterButton>
                                </CounterButtonBox>
                            </StyledTooltip>
                        </Box> :
                        <ButtonPlaceholder /> :
                    null    
            }
            {
                !hideNotApplicables && allowNotApplicable ?
                    hasNotApplicable ?
                        <Box>
                            <StyledTooltip
                                title={
                                    disabled ? 
                                        'Form does not exist in OpenClinica' :
                                        allNotApplicable ? 
                                            'Not Applicable' : 
                                            'Applicable'
                                }
                                followCursor
                            >
                                <CounterButtonBox>
                                    <CounterButton
                                        variant={
                                            allNotApplicable ?
                                                'contained' :
                                                'outlined'
                                        }
                                        color={
                                            allNotApplicable ?
                                                'success' :
                                                'primary'
                                        }
                                        loading={!!verificationLoading || !!loading}
                                        onClick={onNotApplicableClick}
                                        disabled={disabled}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.notApplicable} size="2x" />
                                    </CounterButton>
                                </CounterButtonBox>
                            </StyledTooltip>
                        </Box> :
                        <ButtonPlaceholder /> :
                    null    
            }
        </Container>
    );
};

export default SourceDataFormReviewAndVerificationAllButton;